import { modules } from 'utils/module-loader'
import Emitter from 'utils/emitter'
import isLoaded from 'utils/isLoaded'
import afterPageLoad from 'utils/afterPageLoad'
import scrollToPosts from 'utils/scrollToPosts'

import Nav from 'components/Nav'
import attachNavigationClickEvents from './utils/attachNavigationClickEvents'
import attachOutboundLinkClickEvents from './utils/attachOutboundLinkClickEvents'
import SentryWrapper from './utils/SentryWrapper'

export default class App {
    static Nav

    constructor() {
        new SentryWrapper()
        this.detectFrame()

        this.registerEvents()

        this.initNav()

        this.registerInstances()
        attachNavigationClickEvents()
        attachOutboundLinkClickEvents()

        // A /perspectives/<n>/ URL names a slice of the archive, so a visitor
        // opening one lands on that slice rather than at the top of a hero they
        // have to scroll past to reach what they asked for (IV-1696). On load,
        // because the media above the archive settles its height between
        // DOMContentLoaded and load and moves the target ~130px down.
        afterPageLoad(() => scrollToPosts({behavior: 'auto'}))
    }

    /**
     * @desc Initializes the main nav. Should only occur once
     * to avoid multiple bindings.
     */
    initNav() {
        this.constructor.Nav = new Nav()
    }

    trackScrollDepth() {
        if (!document.body || typeof window.gtag === 'undefined') {
            return
        }
        window.scrollDepth = window.scrollDepth || {}
        let lastMark = window.scrollDepth.lastMark || 0
        let checkMarks = [0, 25, 50, 75, 90]
        let scrollPercent = Math.round((window.scrollY / document.body.clientHeight) * 100)
        let scrollPercentRounded = checkMarks.reduce((prev, curr) => {
            return (Math.abs(curr - scrollPercent) < Math.abs(prev - scrollPercent)) ? curr : prev
        })
        if (scrollPercentRounded !== lastMark) {
            window.scrollDepth.lastMark = scrollPercentRounded
            window.gtag('event', 'scroll_depth', {
                'event_category': 'scroll',
                'event_label': 'scroll_depth',
                'value': scrollPercentRounded,
            })
        }
    }

    /**
     * @desc If serving page within a frame
     * add a class to the html element.
     */
    detectFrame() {
        if (window !== window.top) {
            document.documentElement.classList.add('in-frame')
        }
    }

    /**
     * @desc Create all instances of classes that are paired with
     * javascript components in the modules Array. Components are
     * code-split, so each present module is lazily imported on demand.
     * @return {Promise<Array.<Object>>} Resolves to the instantiated modules.
     */
    async registerInstances() {
        const present = modules.filter((module) =>
            document.querySelector(module.componentClass),
        )

        const instances = await Promise.all(
            present.map(async (module) => {
                const elements = Array.from(
                    document.querySelectorAll(module.componentClass),
                )
                try {
                    const Source = await module.load()
                    return {
                        name: module.name,
                        refs: elements.map((el) => new Source(el)),
                    }
                } catch (error) {
                    console.error(
                        `Failed to load component "${module.name}"`,
                        error,
                    )
                    return { name: module.name, refs: [] }
                }
            }),
        )

        return instances.filter((instanceObj) => instanceObj.refs.length)
    }

    /**
     * @desc - These events get instantiated once and bound to the window
     * so that there aren’t multiple events firing from each page module
     * @fires <App>#[resize]
     * @fires <App>#[scroll]
     * @fires <App>#[keydown]
     */
    registerEvents() {
        /** Fire resize event to emitter when window resizes */
        window.addEventListener('resize', () => {
            Emitter.emit('resize')
        })
        /** Fire scroll event to emitter when window scroll */
        window.addEventListener('scroll', () => {
            this.trackScrollDepth()
            Emitter.emit('scroll')
        })
        /** Fire keydown event to emitter on window keydown */
        window.addEventListener('keydown', (e) => {
            Emitter.emit('keydown', e)
        })
    }


}

isLoaded(App)
